import { addMinutes, differenceInMinutes, parseISO, setHours } from "date-fns";
import { sample, find, flatten, omit, sortBy } from "lodash";
import { applyOffsetIso, LocalVisitRequest, filterToDraggable } from "~/utils/eventsHelper";
import { Action, LocalVisit, LocalVisitResource, LocalShift, LocalCalendarBlock } from "./visitDragDropReducer";
import { CalendarBlockEventStatusEnum, CommandIndexCombinedDataQuery, CommandIndexProvidersQuery, ShiftProviderTravelEnum, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";
import { OrgConfig, VisitResourceRequirement } from "~/providers/OrgConfigProvider";
import { Dispatch } from "react";
import {
  validateProviderShift,
  validateProviderLicense,
  validateProviderServiceLine,
  validateDriveTimeBuffers,
  validateFilterBlocks,
  validateBusinessRules,
  ValidationError,
  ValidationErrorType,
  getProviderType,
  getProviderShiftInDateRange,
  hasStateLicense,
  validateTravelTypeCompatibility
} from "~/utils/visitValidationUtils";
import { IValidateProps } from "@mobiscroll/react/dist/src/core/util/datetime.js";
import { CalendarBlockConflict, calendarBlockIsFilter, GenericCalendarBlock, isAllowedInBlock } from "@caredna-marlin/utils/calendarBlockHelper";
import { getEpisodeGroupLabel, UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";

export function unknownType(type: never): never {
  throw new Error(`Unknown type: ${(type as any)}`);
}

/**
 * Data structure representing the state of a drag-drop operation
 */
export interface VisitDragDropData {
  event: NonNullable<ReturnType<typeof filterToDraggable>>;
  eventStart: Date;
  eventEnd: Date;
  providerShift:  CommandIndexCombinedDataQuery['shift'][number] | null;
  orgConfig: OrgConfig;
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localShifts: LocalShift[];
  localCalendarBlocks: LocalCalendarBlock[];
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  // For multi-resource visits, tracks which provider is assigned to which resource requirement
  assignedProvidersToResourcesMap: Record<number, number | null>;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
}

/**
 * Create VisitDragDropData from event and dependencies
 */
export function createVisitDragDropData(
  event: NonNullable<ReturnType<typeof filterToDraggable>>,
  orgConfig: OrgConfig,
  localVisitResources: LocalVisitResource[],
  localVisits: LocalVisit[],
  localVisitRequests: LocalVisitRequest[],
  localShifts: LocalShift[],
  localCalendarBlocks: LocalCalendarBlock[],
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  bufferBeforeMinutes: number = 0,
  bufferAfterMinutes: number = 0,
): VisitDragDropData {
  const eventStart = parseISO(event.start);
  let eventEnd = parseISO(event.end);

  if (event.type === "visit_request") {
    const localVisitRequest = localVisitRequests.find(vr => vr.id === event.referenceId)!;
    eventEnd = addMinutes(eventStart, localVisitRequest.duration);
  }

  const providerId = event.providerId;
  const shift = providerId ? getProviderShiftInDateRange(providerId, localShifts, event.start, event.end) ?? null : null;

  const providerShift = shift ? {
    ...shift,
    provider: omit(providers.find(p => p.id === providerId)!, "__typename"),
  } : null

  return {
    event,
    eventStart,
    eventEnd,
    providerShift,
    orgConfig,
    localShifts,
    localVisits,
    localVisitResources,
    localVisitRequests,
    localCalendarBlocks,
    providers,
    assignedProvidersToResourcesMap: {},
    bufferBeforeMinutes,
    bufferAfterMinutes,
  };
}

/**
 * Get local visit request from data
 */
export function getLocalVisitRequest(data: VisitDragDropData): LocalVisitRequest {
  return data.localVisitRequests.find((vr) => vr.id === data.event.referenceId)!;
}

/**
 * Get local visit resource from data
 */
export function getLocalVisitResource(data: VisitDragDropData): LocalVisitResource {
  return data.localVisitResources.find(vr => vr.id === data.event.referenceId)!;
}

/**
 * Get local visit from data
 */
export function getLocalVisit(data: VisitDragDropData): LocalVisit {
  return data.localVisits.find(v => v.id === getLocalVisitResource(data)?.visitId)!;
}

/**
 * Get visit type from data
 */
export function getVisitType(data: VisitDragDropData) {
  if (data.event.type === "calendar_block") {
    return null;
  }

  if (data.event.type === "visit_request") {
    return getLocalVisitRequest(data)?.visitType;
  }

  if (data.event.type === "visit_resource") {
    const visitResource = getLocalVisitResource(data);
    if (visitResource?.visit) return data.orgConfig.visitTypes[visitResource.visit.visitTypeId];
  }

  throw new Error('getVisitType: Invalid event type -> ' + data.event.type);
}

/**
 * Find all service lines that contain a given visit type
 */
export function findAllServiceLinesForVisitType(
  visitTypeId: number,
  orgConfig: OrgConfig
): OrgConfig["serviceLines"][string][] {
  return Object.values(orgConfig.serviceLines).filter(
    sl => sl?.visitTypeServiceLines?.some(vtsl => vtsl.visitTypeId === visitTypeId)
  );
}

function parseServiceLineConfig(serviceLine: OrgConfig["serviceLines"][string] | undefined): {
  ignoreDriveTimeShiftStart: boolean;
  ignoreDriveTimeShiftEnd: boolean;
} {
  const defaults = { ignoreDriveTimeShiftStart: false, ignoreDriveTimeShiftEnd: false };
  
  if (!serviceLine?.schedulingConfig) {
    return defaults;
  }
  
  try {
    const config = typeof serviceLine.schedulingConfig === 'string'
      ? JSON.parse(serviceLine.schedulingConfig)
      : serviceLine.schedulingConfig;
    
    return {
      ignoreDriveTimeShiftStart: config.ignoreDriveTimeShiftStart ?? defaults.ignoreDriveTimeShiftStart,
      ignoreDriveTimeShiftEnd: config.ignoreDriveTimeShiftEnd ?? defaults.ignoreDriveTimeShiftEnd,
    };
  } catch {
    return defaults;
  }
}

/**
 * Get the ignore drive time at boundaries config for a drag-drop event
 * Used to determine whether to validate drive time buffers against shift boundaries
 */
export function getIgnoreDriveTimeAtBoundariesConfig(data: VisitDragDropData): { 
  ignoreDriveTimeShiftStart: boolean; 
  ignoreDriveTimeShiftEnd: boolean;
} {
  switch (data.event.type) {
    case "visit_request": {
      const localRequest = getLocalVisitRequest(data);
      // Parse the service line config from raw GQL data
      return parseServiceLineConfig(localRequest?.serviceLine);
    }
    case "visit_resource": {
      const localResource = getLocalVisitResource(data);
      const visitTypeId = localResource.visit?.visitTypeId;
      // TODO: Find actual service line for visit resource not just the visit type
      const serviceLines = visitTypeId ? findAllServiceLinesForVisitType(visitTypeId, data.orgConfig) : [];

      return {
        ignoreDriveTimeShiftStart: serviceLines.some(
          serviceLine => parseServiceLineConfig(serviceLine).ignoreDriveTimeShiftStart
        ),
        ignoreDriveTimeShiftEnd: serviceLines.some(
          serviceLine => parseServiceLineConfig(serviceLine).ignoreDriveTimeShiftEnd
        ),
      };
    }
    case "calendar_block":
      // Calendar blocks don't have service line constraints - skip drive time validation
      return { ignoreDriveTimeShiftStart: true, ignoreDriveTimeShiftEnd: true };
    default:
      return { ignoreDriveTimeShiftStart: false, ignoreDriveTimeShiftEnd: false };
  }
}

/**
 * Check if event is multi-resource
 */
export function isMultiResource(data: VisitDragDropData): boolean {
  if (data.event.type === "calendar_block") return false;
  const visitType = getVisitType(data);
  return (visitType?.visitResourceRequirements?.length ?? 0) > 1;
}

/**
 * Get allowed visit resource requirements
 */
export function getAllowedVisitResourceRequirements(data: VisitDragDropData): VisitResourceRequirement[] {
  if (data.event.type === "visit_request") {
    return getLocalVisitRequest(data).visitType?.visitResourceRequirements || [];
  }

  const localVisitResource = getLocalVisitResource(data);
  return [data.orgConfig.visitResourceRequirements[localVisitResource.visitResourceRequirementId]].filter(vrr => vrr !== undefined);
}

/**
 * Get resource requirement for the event
 * For new visit requests, returns the resource requirement for the multi-resource visit.
 * For existing visits, returns the resource requirement for the visit.
 */
function selectResourceRequirementForMultiResource(data: VisitDragDropData): VisitResourceRequirement | null {
  // if it's NOT a multi-resource then pick the first and only resource requirement.
  if (!isMultiResource(data)) {
    return getLocalVisitRequest(data).visitType?.visitResourceRequirements[0] ?? null;
  }

  const providerType = getProviderType(data.providerShift, data.orgConfig);

  if (!providerType) {
    return null;
  }

  const matchingRequirements = getResourceRequirementsMatchingProviderType(data, providerType);
  if (matchingRequirements.length === 0) return null;
  if (matchingRequirements.length === 1) return matchingRequirements[0];

  // if there are multiple matching requirements with current provider then it needs to determine the best one to use
  return selectResourceRequirementByTravelType(data, matchingRequirements);
}

function getResourceRequirementsMatchingProviderType(data: VisitDragDropData, providerType: { id: number; name: string }) {
  return getLocalVisitRequest(data).visitType?.visitResourceRequirements.filter(rr =>
    rr.providerTypes.some(pt => pt.id === providerType.id)
  ) || [];
}

function selectResourceRequirementByTravelType(
  data: VisitDragDropData,
  matchingRequirements: NonNullable<ReturnType<typeof getLocalVisitRequest>>["visitType"]["visitResourceRequirements"]
): VisitResourceRequirement | null {
  if (!data.providerShift) {
    return null;
  }

  const { providerTravel } = data.providerShift;

  if (providerTravel === ShiftProviderTravelEnum.Either) {
    // if the shift allows either in home or virtual then randomly select one of the matching provider types
    //@TODO: Select the virtual one if shift allows it.
    return sample(matchingRequirements) ?? null;
  }

  if (providerTravel === ShiftProviderTravelEnum.InHomeOnly) {
    return find(matchingRequirements, rr => rr.inHome) ?? null;
  }

  if (providerTravel === ShiftProviderTravelEnum.VirtualOnly) {
    return find(matchingRequirements, rr => !rr.inHome) ?? null;
  }

  return null;
}

/**
 * Get resource requirement for the event
 */
export function getResourceRequirement(data: VisitDragDropData): VisitResourceRequirement | null {
  if (data.event.type === "visit_request") {
    return selectResourceRequirementForMultiResource(data);
  } else if (data.event.type === "visit_resource") {
    const localVisitResource = getLocalVisitResource(data);
    return data.orgConfig.visitResourceRequirements[localVisitResource.visitResourceRequirementId!] ?? null;
  }
  return null;
}

/**
 * Get inHome property from resource requirement
 */
export function getInHome(data: VisitDragDropData): boolean | undefined {
  return getResourceRequirement(data)?.inHome;
}

export function getProviderId(data: VisitDragDropData): number | null {
  return data.event.providerId;
}

/**
 * Get eligible provider types for the event
 */
export function getEligibleProviderTypes(data: VisitDragDropData): number[] {
  if (data.event.type === "visit_request") {
    return getEligibleProviderTypesForVisitRequest(data);
  }

  if (data.event.type === "visit_resource") {
    return getEligibleProviderTypesForVisit(data);
  }

  return [];
}

function getEligibleProviderTypesForVisitRequest(data: VisitDragDropData): number[] {
  if (isMultiResource(data)) {
    // flatten all provider types from all resource requirements
    return flatten(
      getLocalVisitRequest(data).visitType?.visitResourceRequirements.map(vrr =>
        vrr.providerTypes.map(pt => pt.id)
      )
    );
  }

  // single resource requirement
  return getLocalVisitRequest(data).visitType?.visitResourceRequirements[0].providerTypes.map(pt => pt.id) || [];
}

function getEligibleProviderTypesForVisit(data: VisitDragDropData): number[] {
  const localVisitResource = getLocalVisitResource(data);
  const visitResourceRequirement = data.orgConfig.visitResourceRequirements[localVisitResource.visitResourceRequirementId];
  return visitResourceRequirement?.providerTypes.map(pt => pt.id) || [];
}

/**
 * Consolidates all validation logic for visit requests and visit events
 */
export function validateVisitDragDrop(data: VisitDragDropData, careTeamProviderIds: number[]): ValidationError[] {
  const providerId = getProviderId(data);
  if (!providerId) {
    const group = data.localVisitResources.find(vr => vr.id === data.event.referenceId)?.visit.episode?.autoschedulerGroup;

    return [{
      type: ValidationErrorType.UNASSIGNED_PROVIDER,
      message: `Visit not assigned to any provider${group ? ` (${getEpisodeGroupLabel(group)})`: ''}`,
      shouldAllowDrop: false,
      severity: 3,
    }];
  }

  const errors = validateProviderShift(providerId, data.localShifts, data.eventStart, data.eventEnd);

  // We might not have the visit type yet if the orgConfig hasn't loaded
  if (data.event.type === "calendar_block") {
    return errors;
  }

  const visitType = getVisitType(data);
  if (!visitType) {
    return errors;
  }

  const currentProvider = data.providers.find((p: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]) => p.id === providerId);
  if (currentProvider && !currentProvider.active) {
    errors.push({
      type: ValidationErrorType.INACTIVE_PROVIDER,
      message: 'Provider is inactive',
      shouldAllowDrop: false,
    });
  }

  const address = data.event.type === "visit_resource" 
    ? getLocalVisitResource(data).visit 
    : getLocalVisitRequest(data).patient;
  errors.push(...validateFilterBlocks(providerId, visitType.id, address, data.localCalendarBlocks, data.eventStart, data.eventEnd));

  if (data.providerShift) {
    errors.push(...validateBusinessRules(
      data.providerShift,
      data.orgConfig,
      getAllowedVisitResourceRequirements(data),
      !!getInHome(data),
      data.event.type,
      isMultiResource(data),
      careTeamProviderIds
    ));
  }

  // Validate provider licensing for the patient's state
  const patientState = data.event.type === "visit_resource" 
    ? getLocalVisitResource(data).visit?.addressState 
    : getLocalVisitRequest(data).patient?.addressState;
  
  if (currentProvider && patientState) {
    errors.push(...validateProviderLicense(
      currentProvider,
      patientState,
      data.orgConfig,
      getAllowedVisitResourceRequirements(data)
    ));
  }

  // Validate provider service line assignment
  const visitServiceLines = data.event.type === "visit_resource"
    ? (() => {
        const visitTypeId = getLocalVisitResource(data).visit?.visitTypeId;
        if (!visitTypeId) {
          return [];
        }

        return findAllServiceLinesForVisitType(visitTypeId, data.orgConfig).flatMap((serviceLine) => (
          serviceLine
            ? [{
                id: serviceLine.id,
                name: serviceLine.name,
              }]
            : []
        ));
      })()
    : (() => {
        const requestServiceLine = getLocalVisitRequest(data).serviceLine;
        if (!requestServiceLine) {
          return [];
        }

        return [{
          id: requestServiceLine.id,
          name: requestServiceLine.name,
        }];
      })();

  if (currentProvider && visitServiceLines.length) {
    errors.push(...validateProviderServiceLine(
      {
        firstName: currentProvider.firstName,
        lastName: currentProvider.lastName,
        allowedServiceLineIds: Object.values(data.orgConfig.serviceLines)
          .filter((serviceLine) =>
            serviceLine?.visitTypeServiceLines.some((vtsl) =>
              currentProvider.serviceLineProviders.some((slp) =>
                slp.serviceLine?.visitTypeServiceLines.some(
                  (providerVtsl) => providerVtsl.visitTypeId === vtsl.visitTypeId
                )
              )
            )
          )
          .map((serviceLine) => serviceLine!.id),
      },
      visitServiceLines
    ));
  }

  if (data.providerShift && (data.bufferBeforeMinutes > 0 || data.bufferAfterMinutes > 0)) {
    const shiftStart = parseISO(data.providerShift.startTime);
    const shiftEnd = parseISO(data.providerShift.endTime);
    const ignoreConfig = getIgnoreDriveTimeAtBoundariesConfig(data);
    
    errors.push(...validateDriveTimeBuffers(
      data.eventStart,
      data.eventEnd,
      data.bufferBeforeMinutes,
      data.bufferAfterMinutes,
      shiftStart,
      shiftEnd,
      ignoreConfig
    ));
  }

  return errors;
}

/**
 * Get all resource requirements that need provider assignments
 */
export function getAllResourceRequirements(data: VisitDragDropData): VisitResourceRequirement[] {
  if (!isMultiResource(data) || data.event.type !== "visit_request") {
    return [];
  }
  return getLocalVisitRequest(data).visitType?.visitResourceRequirements || [];
}

/**
 * Get all unique provider IDs currently assigned to any resource requirement
 */
export function getAssignedProviderIds(data: VisitDragDropData): (number | null)[] {
  return [...new Set(Object.values(data.assignedProvidersToResourcesMap))];
}

/**
 * Get resource requirements assigned to a specific provider
 */
export function getResourceRequirementsForProvider(data: VisitDragDropData, providerId: number) {
  return Object.entries(data.assignedProvidersToResourcesMap)
    .filter(([, assignedProviderId]) => assignedProviderId === providerId)
    .map(([resourceRequirementId]) => ({
      resourceRequirementId: Number(resourceRequirementId),
      resourceRequirement: getAllResourceRequirements(data).find(rr => rr.id === Number(resourceRequirementId))
    }));
}

/**
 * Check if a provider is already assigned to a specific resource requirement
 */
export function isProviderAssignedToResource(data: VisitDragDropData, resourceRequirementId: number, providerId: number | null): boolean {
  return data.assignedProvidersToResourcesMap[resourceRequirementId] === providerId;
}

/**
 * Check if a provider is assigned to any resource requirement
 */
export function isProviderAssigned(data: VisitDragDropData, providerId: number): boolean {
  return Object.values(data.assignedProvidersToResourcesMap).includes(providerId);
}

/**
 * Find which resource requirement a provider is currently assigned to
 */
export function findProviderCurrentAssignment(data: VisitDragDropData, providerId: number | null): number | null {
  const entry = Object.entries(data.assignedProvidersToResourcesMap)
    .find(([, assignedProviderId]) => assignedProviderId === providerId);
  return entry ? Number(entry[0]) : null;
}

/**
 * Assign a provider to a specific resource requirement
 * Returns a new data object with updated assignments
 * If the provider is already assigned to another resource requirement, they will be unassigned from it
 */
export function assignProviderToResource(
  data: VisitDragDropData,
  resourceRequirementId: number,
  providerId: number | null
): VisitDragDropData {
  // If provider is already assigned to this exact requirement, no need to do anything
  if (isProviderAssignedToResource(data, resourceRequirementId, providerId)) {
    return data;
  }

  // Create a new map with updated assignments
  const newMap = { ...data.assignedProvidersToResourcesMap };

  // Check if provider is assigned to a different resource requirement
  const currentAssignment = findProviderCurrentAssignment(data, providerId);
  if (currentAssignment !== null) {
    // Remove provider from their current assignment
    delete newMap[currentAssignment];
  }

  // Assign provider to the new resource requirement
  newMap[resourceRequirementId] = providerId;

  return {
    ...data,
    assignedProvidersToResourcesMap: newMap,
  };
}

/**
 * Get resource requirements that still need provider assignments
 */
export function getPendingAssignments(data: VisitDragDropData) {
  const assignedIds = Object.keys(data.assignedProvidersToResourcesMap).map(Number);
  return getAllResourceRequirements(data)
    .filter(rr => !assignedIds.includes(rr.id))
    .map(rr => ({
      resourceRequirementId: rr.id,
      resourceRequirement: rr
    }));
}

/**
 * Get currently assigned providers mapped to their resource requirements
 */
export function getAssignedProviders(data: VisitDragDropData) {
  return Object.entries(data.assignedProvidersToResourcesMap).map(([resourceRequirementId, providerId]) => ({
    resourceRequirementId: Number(resourceRequirementId),
    providerId,
    resourceRequirement: getAllResourceRequirements(data).find(rr => rr.id === Number(resourceRequirementId))
  }));
}

/**
 * Check if all resource requirements have been assigned providers
 */
export function isFullyAssigned(data: VisitDragDropData): boolean {
  return getPendingAssignments(data).length === 0;
}

/**
 * Initialize the first provider assignment based on the current provider and resource requirement
 * Returns updated data object if assignment was successful, original data otherwise
 */
export function initializeFirstAssignment(data: VisitDragDropData): VisitDragDropData {
  if (!isMultiResource(data) || data.event.type !== "visit_request") {
    return data;
  }

  const firstResourceRequirement = getResourceRequirement(data);
  if (!firstResourceRequirement) {
    return data;
  }

  return assignProviderToResource(data, firstResourceRequirement.id, getProviderId(data));
}

function visitTypeDuration(resourceRequirements: VisitResourceRequirement[]): number {
  return resourceRequirements
    .map(r => r.offset + r.duration)
    .sort((a, b) => { return b - a; })[0];
}

function scaleVisitResources(resourceRequirements: VisitResourceRequirement[], visitDuration: number, defaultDuration: number): VisitResourceRequirement[] {
  const durationScaleFactor = visitDuration / defaultDuration;

  return resourceRequirements.map(requirement => ({
    ...requirement,
    offset: Math.floor(requirement.offset * durationScaleFactor),
    duration: Math.max(Math.floor(requirement.duration * durationScaleFactor), 1),
  }));
}

function getStartEndTimesWithOffsetForVisitRequest(data: VisitDragDropData, resourceRequirementId: number): { startTime: string; endTime: string; offset: number } {
  if (data.event.type !== "visit_request") {
    throw new Error('getStartEndTimesWithOffsetForVisitRequest: Invalid event type');
  }

  const startTime = `${data.event.start}`;
  const endTime = `${data.event.end}`;

  const localVisitRequest = getLocalVisitRequest(data);
  // whatever the duration was set on the visit request form or existent duration for existent visits
  const duration = localVisitRequest?.duration || differenceInMinutes(new Date(endTime), new Date(startTime));

  const visitResourceRequirements = localVisitRequest.visitType?.visitResourceRequirements || [];

  const actualDuration = visitTypeDuration(visitResourceRequirements);

  const scaledResourceRequirements = scaleVisitResources(visitResourceRequirements, duration ?? actualDuration, actualDuration);

  const requirement = scaledResourceRequirements.find(r => r.id === resourceRequirementId);

  const newStartTime = addMinutes(startTime, requirement?.offset ?? 0);
  const newEndTime = (requirement?.offset ?? 0) + (requirement?.duration ?? actualDuration) == actualDuration ? endTime : addMinutes(newStartTime, requirement?.duration ?? 0);

  return {
    startTime: newStartTime.toISOString(),
    endTime: new Date(newEndTime).toISOString(),
    offset: requirement?.offset ?? 0,
  };
}

/**
 * Assign visit to state (dispatch actions)
 */
export function assignVisitToState(data: VisitDragDropData, dispatch: Dispatch<Action>): void {
  switch (data.event.type) {
    case "visit_resource":
      assignResourceToState(data, dispatch);
      break;
    case "visit_request":
      assignRequestToState(data, dispatch);
      break;
    case "calendar_block":
      assignBlockToState(data, dispatch);
      break;
    default:
      unknownType(data.event.type);
  }
}

function assignRequestToState(data: VisitDragDropData, dispatch: Dispatch<Action>): void {
  const localVisitRequest = getLocalVisitRequest(data);
  const visitId = localVisitRequest.taskId!; // Temp negative id for unpersisted resources

  const createPayload = (resourceRequirement: { id: number }, providerId: number | null, startTime: string, endTime: string): LocalVisitResource => ({
    id: Number(`${Math.abs(data.event.id)}${resourceRequirement.id}`),
    providerId,
    startTime,
    endTime,
    endOfShiftDriveTime: 0,
    interVisitDriveTime: 0,
    startOfShiftDriveTime: 0,
    updatedBy: null,
    provider: providerId ? {
      id: providerId,
      firstName: data.providerShift?.provider?.firstName || "",
      lastName: data.providerShift?.provider?.lastName || "",
      providerTypeId: data.providerShift?.provider?.providerTypeId || 0,
      active: true,
    } : null,
    virtualProviderStatus: null,
    errandStatus: null,
    visitId,
    visitResourceRequirementId: resourceRequirement.id,
    addressCoordinates: null,
    visit: {
      id: visitId,
      careRequirementId: null,
      startTime: data.eventStart.toISOString(),
      endTime: data.eventEnd.toISOString(),
      eventStatus: VisitEventStatusEnum.Scheduled,
      addressTimezone: localVisitRequest.patient?.addressTimezone || '',
      addressCoordinates: localVisitRequest.patient?.addressCoordinates || '',
      addressStreet: localVisitRequest.patient?.addressStreet || '',
      addressLineTwo: localVisitRequest.patient?.addressLineTwo || null,
      addressCity: localVisitRequest.patient?.addressCity || '',
      addressState: localVisitRequest.patient?.addressState || '',
      addressZip: localVisitRequest.patient?.addressZip || '',
      arrivalWindowStartTime: data.eventStart.toISOString(),
      arrivalWindowEndTime: data.eventEnd.toISOString(),
      createdAt: data.eventStart.toISOString(),
      visitTypeId: localVisitRequest.visitType.id,
      manualEdits: false,
      visitWarningLogId: null,
      updatedBy: null,
      cancellationReason: null,
      patient: {
        // The patient might have come from a Task (visit request) or from a visit); we care about
        // the fields, of course, but not the ultimate GQL source of the object
        ...omit(localVisitRequest.patient, "__typename"),
        patientIdentifiers: [...localVisitRequest.patient?.patientIdentifiers?.map(i => omit(i, "__typename")) || []],
        careTeamProviders: localVisitRequest.patient?.careTeamProviders?.map(provider => ({
          ...omit(provider, "__typename")
        })) || [],
      },
      episode: {
        id: 1,
        serviceLineId: 1,
        autoschedulerGroup: null,
        precautions: [],
      },
      eventLog: [],
    }
  });

  if (isMultiResource(data)) {
    const assignments = data.assignedProvidersToResourcesMap;

    if (Object.keys(assignments).length === 0) {
      throw new Error('assignToState: No provider assignments found for multi-resource visit');
    }

    Object.entries(assignments).forEach(([resourceRequirementId, providerId]) => {
      const resourceRequirement = getAllResourceRequirements(data).find(rr => rr.id === Number(resourceRequirementId));
      if (!resourceRequirement) return;

      const { startTime, endTime } = getStartEndTimesWithOffsetForVisitRequest(data, Number(resourceRequirementId));

      dispatch({
        type: 'ADD_RESOURCE',
        payload: createPayload(resourceRequirement, providerId, startTime, endTime),
      });
    });
  } else {
    const resourceRequirement = getResourceRequirement(data);
    if (!resourceRequirement) return;
    dispatch({
      type: 'ADD_RESOURCE',
      payload: createPayload(resourceRequirement, getProviderId(data), data.eventStart.toISOString(), data.eventEnd.toISOString()),
    });
  }

  dispatch({
    type: 'STORE_VISIT_METADATA',
    payload: {
      visitId: visitId.toString(),
      metaData: {
        taskId: localVisitRequest.taskId,
        serviceLineId: localVisitRequest.serviceLine?.id ?? 0,
        serviceIds: [],
        address: {
          addressStreet: localVisitRequest.patient?.addressLineTwo || '',
          addressCity: localVisitRequest.patient?.addressCity || '',
          addressState: localVisitRequest.patient?.addressState || '',
          addressZip: localVisitRequest.patient?.addressZip || '',
        },
        duration: differenceInMinutes(data.eventEnd, data.eventStart),
      },
    },
  });
}

function assignResourceToState(data: VisitDragDropData, dispatch: Dispatch<Action>): void {
  const localVisit = getLocalVisit(data);
  const localVisitResource = getLocalVisitResource(data);
  const otherVisitResources = localVisit.visitResources.filter(
    vr => vr.id !== localVisitResource.id
  );

  otherVisitResources.forEach(vr => {
    dispatch({
      type: 'MODIFY_RESOURCES',
      payload: {
        [vr.id.toString()]: {
          startTime: applyOffsetIso(localVisitResource.startTime, data.event.start, vr.startTime),
          endTime: applyOffsetIso(localVisitResource.endTime, data.event.end, vr.endTime),
        }
      }
    });
  });

  // @TODO: get back to this for allowing more changes besides start/end times
  dispatch({
    type: 'MODIFY_RESOURCES',
    payload: {
      [data.event.referenceId.toString()]: {
        startTime: data.eventStart.toISOString(),
        endTime: data.eventEnd.toISOString(),
        providerId: getProviderId(data),
      }
    }
  });
}

function assignBlockToState(data: VisitDragDropData, dispatch: Dispatch<Action>): void {
  const providerId = getProviderId(data);
  if (!providerId) {
    console.error("Found a null providerId on a calendar block; this should never happen.", data);
    return;
  }

  dispatch({
    type: 'MODIFY_CALENDAR_BLOCKS',
    payload: {
      [data.event.referenceId.toString()]: {
        startTime: data.eventStart.toISOString(),
        endTime: data.eventEnd.toISOString(),
        providerId,
      }
    }
  });
}

export function invalidBlock(start: Date, end: Date, resource: number | null): IValidateProps & { resource: number, cssClass: string } {
  return {
    start,
    end,
    resource: resource ?? UNASSIGNED_PROVIDER_ID,
    cssClass: "invalid-drop",
  };
}

function canDropOnProvider(data: VisitDragDropData, provider: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]): boolean {
  if (!provider.active) return false;
  switch (data.event.type) {
    case "visit_resource":
    case "visit_request":
      return getEligibleProviderTypes(data).includes(provider.providerTypeId);
    case "calendar_block":
      return true;
    default:
      throw new Error(`Unknown type: ${String(data.event.type)}`);
  }
}

function canDropInBlock(data: VisitDragDropData, cb: GenericCalendarBlock): boolean {
  if (!calendarBlockIsFilter(cb)) {
    return true;
  }

  // Calendar blocks don't have visit type restrictions
  if (data.event.type === "calendar_block") {
    return true;
  }

  const visitType = getVisitType(data);
  switch (data.event.type) {
    case "visit_resource":
      return !!visitType && isAllowedInBlock(visitType.id, getLocalVisit(data), cb) === CalendarBlockConflict.ALLOWED;
    case "visit_request": {
      const patient = getLocalVisitRequest(data).patient;
      return !!visitType && !!patient && isAllowedInBlock(visitType.id, patient, cb) === CalendarBlockConflict.ALLOWED;
    }
    default:
      throw new Error(`Unknown type: ${String(data.event.type)}`);
  }
}

export function invalidDropBlockers(
  data: VisitDragDropData | null,
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"],
  calendarDateTime: Date,
  orgConfig?: { providerTypes: Record<number, { requireStateLicense?: boolean }> },
  isOrganizationalUnitDropAllowed?: (data: VisitDragDropData, providerId: number) => boolean,
): IValidateProps[] {
  const blocks: IValidateProps[] = [];
  let inactiveProvidersBlocks: IValidateProps[] = [];

  const inactiveProviders = providers.filter((p) => !p.active);
  if (inactiveProviders?.length) {
    const dayStart = setHours(calendarDateTime, 0);
    const dayEnd = setHours(calendarDateTime, 24);
    inactiveProvidersBlocks = [...inactiveProviders.map((p) => {
      return {
        start: dayStart,
        end: dayEnd,
        resource: p.id,
        cssClass: "inactive-provider",
      }
    })]
  }

  if (!data) { return [...inactiveProvidersBlocks]; }

  let patientState: string | null | undefined = null;
  switch (data.event.type) {
    case "visit_request":
      patientState = getLocalVisitRequest(data).patient?.addressState;
      break;
    case "visit_resource":
      patientState = getLocalVisitResource(data).visit?.addressState;
      break;
  }

  // block dropping on unassigned provider
  blocks.push(invalidBlock(setHours(calendarDateTime, 0), setHours(calendarDateTime, 24), null));

  providers.forEach(provider => {
    const dayStart = setHours(calendarDateTime, 0);
    const dayEnd = setHours(calendarDateTime, 24);

    if (!canDropOnProvider(data, provider) || isOrganizationalUnitDropAllowed?.(data, provider.id) === false) {
      blocks.push(invalidBlock(dayStart, dayEnd, provider.id));
      return;
    }

    if (patientState && orgConfig && provider.providerTypeId) {
      const providerType = orgConfig.providerTypes[provider.providerTypeId];
      if (providerType?.requireStateLicense) {
        const isLicensed = hasStateLicense(provider.traits, patientState);

        if (!isLicensed) {
          blocks.push(invalidBlock(dayStart, dayEnd, provider.id));
          return;
        }
      }
    }

    const shifts = sortBy(data.localShifts.filter(s => {
      const shiftProviderMatch = s.providerId === provider.id && s.status === "confirmed";

      if (!shiftProviderMatch) return false;
      
      if (isMultiResource(data)) {
        return getAllowedVisitResourceRequirements(data).some((vr) => !validateTravelTypeCompatibility(vr.inHome, s.providerTravel).length)
      }

      return !validateTravelTypeCompatibility(getInHome(data), s.providerTravel).length;
    }).map(s => ({
      start: new Date(s.startTime),
      end: new Date(s.endTime),
    })), "start");

    blocks.push(invalidBlock(dayStart, shifts[0]?.start || dayEnd, provider.id));

    shifts.forEach((shift, i) => {
      const next = shifts[i + 1];
      if (next && next.start > shift.end) {
        blocks.push(invalidBlock(shift.end, next.start, provider.id));
      }
    });

    if (shifts.length) {
      blocks.push(invalidBlock(shifts[shifts.length - 1].end, dayEnd, provider.id));
    }
  });

  data.localCalendarBlocks
    .filter((cb) => CalendarBlockEventStatusEnum.Canceled !== cb.eventStatus)
    .forEach(calendarBlock => {
      const cb = { ...calendarBlock, startTime: new Date(calendarBlock.startTime), endTime: new Date(calendarBlock.endTime) }
      if (!canDropInBlock(data, cb)) {
        blocks.push(invalidBlock(cb.startTime, cb.endTime, cb.providerId))
      }
    });

  return [...blocks, ...inactiveProvidersBlocks];
}

export interface PendingEventPosition {
  eventId: number;
  startTime: string;
  endTime: string;
  providerId: number | null;
  eventType: 'visit_resource' | 'calendar_block';
}

export interface ApplyPendingPositionResult<VR, CB> {
  visitResources: VR[];
  calendarBlocks: CB[];
  shouldClearPending: boolean;
}

export function applyPendingPosition<
  VR extends { id: number; startTime: string; providerId: number | null },
  CB extends { id: number; startTime: string; providerId: number }
>(
  visitResources: VR[],
  calendarBlocks: CB[],
  pending: PendingEventPosition | null
): ApplyPendingPositionResult<VR, CB> {
  if (!pending) {
    return { visitResources, calendarBlocks, shouldClearPending: false };
  }

  if (pending.eventType === 'visit_resource') {
    const actualResource = visitResources.find(r => r.id === pending.eventId);
    if (!actualResource) {
      return { visitResources, calendarBlocks, shouldClearPending: false };
    }

    if (actualResource.startTime === pending.startTime && actualResource.providerId === pending.providerId) {
      return { visitResources, calendarBlocks, shouldClearPending: true };
    }

    const updatedResources = visitResources.map(r => {
      if (r.id === pending.eventId) {
        return { ...r, startTime: pending.startTime, endTime: pending.endTime, providerId: pending.providerId };
      }
      return r;
    });

    return { visitResources: updatedResources, calendarBlocks, shouldClearPending: false };
  }

  if (pending.eventType === 'calendar_block') {
    const actualBlock = calendarBlocks.find(b => b.id === pending.eventId);
    if (!actualBlock) {
      return { visitResources, calendarBlocks, shouldClearPending: false };
    }

    if (actualBlock.startTime === pending.startTime && actualBlock.providerId === pending.providerId) {
      return { visitResources, calendarBlocks, shouldClearPending: true };
    }

    const updatedBlocks = calendarBlocks.map(b => {
      if (b.id === pending.eventId) {
        return { ...b, startTime: pending.startTime, endTime: pending.endTime, providerId: pending.providerId };
      }
      return b;
    });

    return { visitResources, calendarBlocks: updatedBlocks, shouldClearPending: false };
  }

  return { visitResources, calendarBlocks, shouldClearPending: false };
}

export interface DriveTimeFields {
  startOfShiftDriveTime?: number | null;
  interVisitDriveTime?: number | null;
  endOfShiftDriveTime?: number | null;
}

export function mergeDriveTimesWithFrozen<
  VR extends { id: number } & DriveTimeFields,
  CB extends { id: number } & DriveTimeFields
>(
  current: { visitResources: VR[]; calendarBlocks: CB[] },
  frozen: { visitResources: VR[]; calendarBlocks: CB[] }
): { visitResources: VR[]; calendarBlocks: CB[] } {
  const mergedVisitResources = current.visitResources.map(currentResource => {
    const frozenResource = frozen.visitResources.find(fr => fr.id === currentResource.id);
    if (!frozenResource) {
      return currentResource;
    }
    return {
      ...currentResource,
      startOfShiftDriveTime: frozenResource.startOfShiftDriveTime,
      interVisitDriveTime: frozenResource.interVisitDriveTime,
      endOfShiftDriveTime: frozenResource.endOfShiftDriveTime,
    };
  });

  const mergedCalendarBlocks = current.calendarBlocks.map(currentBlock => {
    const frozenBlock = frozen.calendarBlocks.find(fb => fb.id === currentBlock.id);
    if (!frozenBlock) {
      return currentBlock;
    }
    return {
      ...currentBlock,
      startOfShiftDriveTime: frozenBlock.startOfShiftDriveTime,
      interVisitDriveTime: frozenBlock.interVisitDriveTime,
      endOfShiftDriveTime: frozenBlock.endOfShiftDriveTime,
    };
  });

  return { visitResources: mergedVisitResources, calendarBlocks: mergedCalendarBlocks };
}
